import { useEffect, useRef } from "react";
import * as PIXI from "pixi.js";

import {
    HAIR_COLORS,
    COMPLEXION_COLORS,
    EYE_COLORS,
    CLOTHING_COLORS
} from "../../constants/characterColors";

import "./layeredPixiImage.css";

export default function LayeredPixiImage({ layers, zombie, layerOptions = {} }) {
    const containerRef = useRef(null);

    useEffect(() => {
        let app;
        let destroyed = false;

        async function initPixi() {
            if (!zombie || !layers || !containerRef.current) return;

            const firstTexture = await PIXI.Assets.load(
                layers.background ||
                layers.drawing ||
                layers.clothing ||
                layers.complexion ||
                layers.eyes ||
                layers.hair
            );

            if (destroyed || !containerRef.current) return;

            const width = containerRef.current.clientWidth;
            const aspectRatio = firstTexture.height / firstTexture.width;
            const height = width * aspectRatio;

            app = new PIXI.Application();

            await app.init({
                width,
                height,
                backgroundAlpha: 0,
                antialias: true
            });

            if (destroyed || !containerRef.current) {
                app.destroy(true);
                return;
            }

            containerRef.current.innerHTML = "";
            containerRef.current.appendChild(app.canvas);

            const layerData = [
                {
                    src: layers.background,
                    tint: EYE_COLORS[zombie.eye_color] || 0x4b2a16,
                    alpha: 0.25
                },
                {
                    src: layers.clothing,
                    tint: CLOTHING_COLORS[zombie.clothing_color] || 0x8c2f1b
                },
                {
                    src: layers.complexion,
                    tint: COMPLEXION_COLORS[zombie.complexion] || 0xf2cdb8,
                    alpha: layerOptions.complexion?.alpha
                },
                {
                    src: layers.eyes,
                    tint: EYE_COLORS[zombie.eye_color] || 0x4b2a16
                },
                {
                    src: layers.hair,
                    tint: HAIR_COLORS[zombie.hair_color] || 0x5a2f16
                },
                {
                    src: layers.drawing,
                    isDrawing: true
                }
            ].filter((layer) => layer.src);

            for (const layer of layerData) {
                if (destroyed || !app?.stage) return;

                const texture = await PIXI.Assets.load(layer.src);

                if (destroyed || !app?.stage) return;

                const sprite = new PIXI.Sprite(texture);

                sprite.anchor.set(0.5);
                sprite.position.set(width / 2, height / 2);

                const scale = Math.min(
                    width / texture.width,
                    height / texture.height
                );

                sprite.scale.set(scale);

                if (layer.tint !== undefined) {
                    sprite.tint = layer.tint;
                }

                if (layer.alpha !==undefined) {
                    sprite.alpha = layer.alpha;
                }

                if (layer.isDrawing) {
                    sprite.alpha = 0.9
                }

                app.stage.addChild(sprite);
            }
        }

        initPixi();

        return () => {
            destroyed = true;
            
            if (app) app.destroy(true);
        };
    }, [layers, zombie, layerOptions]);

    return <div className="layered-pixi-image" ref={containerRef} />;
}